import {Inject, Injectable} from '@angular/core';
import {Meta, Title} from '@angular/platform-browser';
import {DOCUMENT} from '@angular/common';

const BASE_URL = 'https://memechua.com';
const AUTHOR = 'mtuanv, ductinhv, haianhphung';
const LOGO_URL = 'https://memechua.com/assets/images/meme_logo.png';

@Injectable({
  providedIn: 'root'
})
export class SeoService {
  constructor(
    private meta: Meta,
    private titleService: Title,
    @Inject(DOCUMENT) private doc: Document,
  ) {
  }

  // Points search engines at the canonical URL for the current page so infinite-scroll / query
  // string variants don't get indexed as duplicate content. SSR-safe: runs against the server
  // document during render, so crawlers see the tag in the initial HTML.
  private setCanonical(url: string): void {
    let link = this.doc.head.querySelector("link[rel='canonical']") as HTMLLinkElement | null;
    if (!link) {
      link = this.doc.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.doc.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }

  private setOgType(type: 'website' | 'article'): void {
    this.meta.updateTag({property: 'og:type', content: type});
  }

  // 'follow' is kept even when noindex so link equity still flows through search-result / 404
  // pages. Always set explicitly (not just on noindex pages) so SPA navigation from a noindex
  // page back to an indexable one resets the directive instead of leaving 'noindex' behind.
  private setRobots(indexable: boolean): void {
    this.meta.updateTag({name: 'robots', content: indexable ? 'index, follow' : 'noindex, follow'});
  }

  // Replaces the single per-page JSON-LD block (id="structured-data"). Passing null clears it,
  // so navigating from a detail page (with schema) to a listing page (without) leaves nothing
  // stale behind. The site-wide Organization/WebSite graph lives statically in index.html.
  private setStructuredData(data: object | null): void {
    let script = this.doc.getElementById('structured-data') as HTMLScriptElement | null;
    if (!data) {
      script?.remove();
      return;
    }
    if (!script) {
      script = this.doc.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.setAttribute('id', 'structured-data');
      this.doc.head.appendChild(script);
    }
    script.textContent = JSON.stringify(data);
  }

  private capitalize(value: string): string {
    return value ? value.charAt(0).toUpperCase() + value.slice(1) : '';
  }

  setMetaHome() {
    const title = 'Tải Ảnh Meme Hài Hước - Hàng Ngày Cập Nhật | Meme Chúa';
    const description = 'Khám phá và tải xuống những bức ảnh meme hài hước mỗi ngày từ trang chủ của chúng tôi. Duyệt qua bộ sưu tập đa dạng và độc đáo, với công cụ tìm kiếm tiện lợi.';
    this.titleService.setTitle(title);
    this.meta.updateTag({name: 'author', content: AUTHOR});
    this.meta.updateTag({name: 'description', content: description});
    this.meta.updateTag({property: 'og:title', content: title});
    this.meta.updateTag({property: 'og:description', content: description});
    this.meta.updateTag({property: 'og:image', content: LOGO_URL});
    this.meta.updateTag({property: 'og:image:secure_url', content: LOGO_URL});
    this.meta.updateTag({property: 'og:url', content: `${BASE_URL}/`});
    this.meta.updateTag({property: 'twitter:title', content: title});
    this.meta.updateTag({property: 'twitter:image', content: LOGO_URL});
    this.meta.updateTag({property: 'twitter:description', content: description});
    this.meta.updateTag({property: 'twitter:url', content: `${BASE_URL}/`});
    this.setOgType('website');
    this.setCanonical(`${BASE_URL}/`);
    this.setRobots(true);
    this.setStructuredData(null);
  }

  setMetaMeme(name: any, slug: any, imgUrl: any, detail: any = null) {
    const displayName = this.capitalize(name);
    const title = `${displayName} - Ảnh Meme Hài Hước | Meme Chúa`;
    const description = `Meme ${displayName}. Xem, tải xuống và chia sẻ bức ảnh này. Khám phá thêm nhiều meme khác trên trang web của chúng tôi.`;
    const pageUrl = `${BASE_URL}/meme/${slug}`;
    this.titleService.setTitle(title);
    this.meta.updateTag({name: 'author', content: AUTHOR});
    this.meta.updateTag({name: 'description', content: description});
    this.meta.updateTag({property: 'og:title', content: title});
    this.meta.updateTag({property: 'og:description', content: description});
    this.meta.updateTag({property: 'og:image', content: imgUrl});
    this.meta.updateTag({property: 'og:image:secure_url', content: imgUrl});
    this.meta.updateTag({property: 'og:url', content: pageUrl});
    this.meta.updateTag({property: 'twitter:title', content: title});
    this.meta.updateTag({property: 'twitter:image', content: imgUrl});
    this.meta.updateTag({property: 'twitter:description', content: description});
    this.meta.updateTag({property: 'twitter:url', content: pageUrl});
    this.setOgType('article');
    this.setCanonical(pageUrl);
    this.setRobots(true);
    this.setStructuredData({
      '@context': 'https://schema.org',
      '@type': 'ImageObject',
      name: displayName,
      description,
      contentUrl: imgUrl,
      url: pageUrl,
      ...(detail?.created_at ? {uploadDate: detail.created_at, datePublished: detail.created_at} : {}),
      representativeOfPage: true,
      isPartOf: {'@id': `${BASE_URL}/#website`},
    });
  }

  setMetaShopee() {
    const title = 'Shopee Nào Bro!!! | Meme Chúa';
    const description = 'Tổng hợp sản phẩm Shopee hot. Xem thêm nhiều deal thú vị trên trang web của chúng tôi.';
    const pageUrl = `${BASE_URL}/shopee`;
    this.titleService.setTitle(title);
    this.meta.updateTag({name: 'author', content: AUTHOR});
    this.meta.updateTag({name: 'description', content: description});
    this.meta.updateTag({property: 'og:title', content: title});
    this.meta.updateTag({property: 'og:description', content: description});
    this.meta.updateTag({property: 'og:image', content: LOGO_URL});
    this.meta.updateTag({property: 'og:image:secure_url', content: LOGO_URL});
    this.meta.updateTag({property: 'og:url', content: pageUrl});
    this.meta.updateTag({property: 'twitter:title', content: title});
    this.meta.updateTag({property: 'twitter:image', content: LOGO_URL});
    this.meta.updateTag({property: 'twitter:description', content: description});
    this.meta.updateTag({property: 'twitter:url', content: pageUrl});
    this.setOgType('website');
    this.setCanonical(pageUrl);
    this.setRobots(true);
    this.setStructuredData(null);
  }

  setMetaNewsList() {
    const title = 'Tin Tức | Meme Chúa';
    const description = 'Kênh tin tức danh cho giới trẻ. Đọc thêm để khám phá nhiều thông tin thú vị trên trang web của chúng tôi.';
    const pageUrl = `${BASE_URL}/tin-tuc`;
    this.titleService.setTitle(title);
    this.meta.updateTag({name: 'author', content: AUTHOR});
    this.meta.updateTag({name: 'description', content: description});
    this.meta.updateTag({property: 'og:title', content: title});
    this.meta.updateTag({property: 'og:description', content: description});
    this.meta.updateTag({property: 'og:image', content: LOGO_URL});
    this.meta.updateTag({property: 'og:image:secure_url', content: LOGO_URL});
    this.meta.updateTag({property: 'og:url', content: pageUrl});
    this.meta.updateTag({property: 'twitter:title', content: title});
    this.meta.updateTag({property: 'twitter:image', content: LOGO_URL});
    this.meta.updateTag({property: 'twitter:description', content: description});
    this.meta.updateTag({property: 'twitter:url', content: pageUrl});
    this.setOgType('website');
    this.setCanonical(pageUrl);
    this.setRobots(true);
    this.setStructuredData(null);
  }

  setMetaNewsDetail(name: any, slug: any, imgUrl: any, detail: any = null) {
    const displayName = this.capitalize(name);
    const title = `${displayName} - Tin Tức | Meme Chúa`;
    const description = `${displayName}. Xem và chia sẻ bài viết này. Đọc thêm để khám phá nhiều thông tin thú vị trên trang web của chúng tôi.`;
    const pageUrl = `${BASE_URL}/tin-tuc/${slug}`;
    this.titleService.setTitle(title);
    this.meta.updateTag({name: 'author', content: AUTHOR});
    this.meta.updateTag({name: 'description', content: description});
    this.meta.updateTag({property: 'og:title', content: title});
    this.meta.updateTag({property: 'og:description', content: description});
    this.meta.updateTag({property: 'og:image', content: imgUrl});
    this.meta.updateTag({property: 'og:image:secure_url', content: imgUrl});
    this.meta.updateTag({property: 'og:url', content: pageUrl});
    this.meta.updateTag({property: 'twitter:title', content: title});
    this.meta.updateTag({property: 'twitter:image', content: imgUrl});
    this.meta.updateTag({property: 'twitter:description', content: description});
    this.meta.updateTag({property: 'twitter:url', content: pageUrl});
    this.setOgType('article');
    this.setCanonical(pageUrl);
    this.setRobots(true);
    this.setStructuredData({
      '@context': 'https://schema.org',
      '@type': 'NewsArticle',
      headline: displayName,
      image: [imgUrl],
      ...(detail?.created_at ? {datePublished: detail.created_at} : {}),
      dateModified: detail?.updated_at || detail?.created_at || undefined,
      author: {'@type': 'Organization', name: 'Meme Chúa', url: BASE_URL},
      publisher: {
        '@type': 'Organization',
        name: 'Meme Chúa',
        logo: {'@type': 'ImageObject', url: LOGO_URL},
      },
      mainEntityOfPage: {'@type': 'WebPage', '@id': pageUrl},
    });
  }

  setMetaTag(name: any, slug: any) {
    const displayName = this.capitalize(name);
    const title = `${displayName} - Ảnh Meme Hài Hước | Meme Chúa`;
    const description = `Tag ${displayName}. Xem, tải xuống và chia sẻ những bức ảnh hài hước về '${displayName}'.`;
    const pageUrl = `${BASE_URL}/tag/${slug}`;
    this.titleService.setTitle(title);
    this.meta.updateTag({name: 'author', content: AUTHOR});
    this.meta.updateTag({name: 'description', content: description});
    this.meta.updateTag({property: 'og:title', content: title});
    this.meta.updateTag({property: 'og:description', content: description});
    this.meta.updateTag({property: 'og:image', content: LOGO_URL});
    this.meta.updateTag({property: 'og:image:secure_url', content: LOGO_URL});
    this.meta.updateTag({property: 'og:url', content: pageUrl});
    this.meta.updateTag({property: 'twitter:title', content: title});
    this.meta.updateTag({property: 'twitter:image', content: LOGO_URL});
    this.meta.updateTag({property: 'twitter:description', content: description});
    this.meta.updateTag({property: 'twitter:url', content: pageUrl});
    this.setOgType('website');
    this.setCanonical(pageUrl);
    this.setRobots(true);
    this.setStructuredData(null);
  }

  setMetaSearch(name: any) {
    const displayName = this.capitalize(name);
    const title = `Kết quả tìm kiếm cho: ${displayName} | Meme Chúa`;
    const description = `Khám phá những hình ảnh hài hước về '${displayName}'. Xem, tải xuống và chia sẻ bức ảnh này. Khám phá thêm nhiều meme khác trên trang web của chúng tôi.`;
    const pageUrl = `${BASE_URL}/search/${displayName}`;
    this.titleService.setTitle(title);
    this.meta.updateTag({name: 'author', content: AUTHOR});
    this.meta.updateTag({name: 'description', content: description});
    this.meta.updateTag({property: 'og:title', content: title});
    this.meta.updateTag({property: 'og:description', content: description});
    this.meta.updateTag({property: 'og:image', content: LOGO_URL});
    this.meta.updateTag({property: 'og:image:secure_url', content: LOGO_URL});
    this.meta.updateTag({property: 'og:url', content: pageUrl});
    this.meta.updateTag({property: 'twitter:title', content: title});
    this.meta.updateTag({property: 'twitter:image', content: LOGO_URL});
    this.meta.updateTag({property: 'twitter:description', content: description});
    this.meta.updateTag({property: 'twitter:url', content: pageUrl});
    this.setOgType('website');
    this.setCanonical(pageUrl);
    // Search-result pages are thin/near-duplicate — keep them out of the index but let crawlers
    // follow through to the real meme pages.
    this.setRobots(false);
    this.setStructuredData(null);
  }

  setMetaContact() {
    const title = 'Liên Hệ | Meme Chúa';
    const description = 'Khám phá và tải xuống những bức ảnh meme hài hước mỗi ngày từ trang chủ của chúng tôi. Duyệt qua bộ sưu tập đa dạng và độc đáo, với công cụ tìm kiếm tiện lợi.';
    const pageUrl = `${BASE_URL}/contact`;
    this.titleService.setTitle(title);
    this.meta.updateTag({name: 'author', content: AUTHOR});
    this.meta.updateTag({name: 'description', content: description});
    this.meta.updateTag({property: 'og:title', content: title});
    this.meta.updateTag({property: 'og:description', content: description});
    this.meta.updateTag({property: 'og:image', content: LOGO_URL});
    this.meta.updateTag({property: 'og:image:secure_url', content: LOGO_URL});
    this.meta.updateTag({property: 'og:url', content: pageUrl});
    this.meta.updateTag({property: 'twitter:title', content: title});
    this.meta.updateTag({property: 'twitter:image', content: LOGO_URL});
    this.meta.updateTag({property: 'twitter:description', content: description});
    this.meta.updateTag({property: 'twitter:url', content: pageUrl});
    this.setOgType('website');
    this.setCanonical(pageUrl);
    this.setRobots(true);
    this.setStructuredData(null);
  }

  setMetaTermsPrivacy() {
    const title = 'Điều khoản & Quyền riêng tư | Meme Chúa';
    const description = 'Khám phá và tải xuống những bức ảnh meme hài hước mỗi ngày từ trang chủ của chúng tôi. Duyệt qua bộ sưu tập đa dạng và độc đáo, với công cụ tìm kiếm tiện lợi.';
    const pageUrl = `${BASE_URL}/terms-privacy`;
    this.titleService.setTitle(title);
    this.meta.updateTag({name: 'author', content: AUTHOR});
    this.meta.updateTag({name: 'description', content: description});
    this.meta.updateTag({property: 'og:title', content: title});
    this.meta.updateTag({property: 'og:description', content: description});
    this.meta.updateTag({property: 'og:image', content: LOGO_URL});
    this.meta.updateTag({property: 'og:image:secure_url', content: LOGO_URL});
    this.meta.updateTag({property: 'og:url', content: pageUrl});
    this.meta.updateTag({property: 'twitter:title', content: title});
    this.meta.updateTag({property: 'twitter:image', content: LOGO_URL});
    this.meta.updateTag({property: 'twitter:description', content: description});
    this.meta.updateTag({property: 'twitter:url', content: pageUrl});
    this.setOgType('website');
    this.setCanonical(pageUrl);
    this.setRobots(true);
    this.setStructuredData(null);
  }

  setMetaNotFound() {
    const title = 'Trang không tìm thấy | Meme Chúa';
    const description = 'Khám phá và tải xuống những bức ảnh meme hài hước mỗi ngày từ trang chủ của chúng tôi. Duyệt qua bộ sưu tập đa dạng và độc đáo, với công cụ tìm kiếm tiện lợi.';
    const pageUrl = `${BASE_URL}/not-found`;
    this.titleService.setTitle(title);
    this.meta.updateTag({name: 'author', content: AUTHOR});
    this.meta.updateTag({name: 'description', content: description});
    this.meta.updateTag({property: 'og:title', content: title});
    this.meta.updateTag({property: 'og:description', content: description});
    this.meta.updateTag({property: 'og:image', content: LOGO_URL});
    this.meta.updateTag({property: 'og:image:secure_url', content: LOGO_URL});
    this.meta.updateTag({property: 'og:url', content: pageUrl});
    this.meta.updateTag({property: 'twitter:title', content: title});
    this.meta.updateTag({property: 'twitter:image', content: LOGO_URL});
    this.meta.updateTag({property: 'twitter:description', content: description});
    this.meta.updateTag({property: 'twitter:url', content: pageUrl});
    this.setOgType('website');
    this.setCanonical(pageUrl);
    this.setRobots(false);
    this.setStructuredData(null);
  }
}
